import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Home,
  History,
  Tv,
  HelpOutline,
  PowerSettingsNew,
  Settings as SettingsIcon,
  AttachMoney,
  People,
  Language as LanguageIcon,
  Image as ImageIcon,
  Devices as DevicesIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import logoGemini from '/logo-gemini.png';

interface User {
  id: string;
  username: string;
  role: string;
}

interface SidebarProps {
  user: User | null;
  onLogout: () => void;
}

const settingsMenu = [
  { label: 'Devices', icon: <DevicesIcon />, path: '/settings/devices' },
  { label: 'Price', icon: <AttachMoney />, path: '/settings/price' },
  { label: 'Users', icon: <People />, path: '/settings/users' },
  { label: 'Language', icon: <LanguageIcon />, path: '/settings/language' },
  { label: 'Slider', icon: <ImageIcon />, path: '/settings/slider' },
];

export const Sidebar = ({ user, onLogout }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPage = location.pathname.replace('/', '');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const getButtonStyle = (page: string) => ({
    color: 'white',
    mb: 2,
    bgcolor: currentPage === page ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
    '&:hover': {
      bgcolor: 'rgba(255, 255, 255, 0.1)',
    },
  });

  const handleSettingsClick = () => {
    setSettingsOpen((prev) => !prev);
    if (!location.pathname.startsWith('/settings')) {
      navigate('/settings/devices');
    }
  };

  // Determine if a settings submenu item is active
  const isSettingsActive = location.pathname.startsWith('/settings');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Box sx={{
        width: 60,
        bgcolor: '#3730f2',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 3,
        minHeight: '100vh',
        zIndex: 2,
      }}>
        <Avatar
          sx={{
            bgcolor: 'white',
            width: 48,
            height: 48,
            mb: 4,
          }}
          src={logoGemini}
          alt="Gemini Logo"
        />
        <IconButton
          sx={getButtonStyle('dashboard')}
          onClick={() => {
            setSettingsOpen(false);
            navigate('/dashboard');
          }}
        >
          <Home />
        </IconButton>
        <IconButton
          sx={getButtonStyle('history')}
          onClick={() => {
            setSettingsOpen(false);
            navigate('/history');
          }}
        >
          <History />
        </IconButton>
        <IconButton
          sx={getButtonStyle('devices')}
          onClick={() => {
            setSettingsOpen(false);
            navigate('/devices');
          }}
        >
          <Tv />
        </IconButton>
        <IconButton
          sx={{
            ...getButtonStyle('settings'),
            bgcolor: settingsOpen || isSettingsActive ? 'rgba(255,255,255,0.2)' : 'transparent',
          }}
          onClick={handleSettingsClick}
        >
          <SettingsIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }} />
        <Divider sx={{ bgcolor: 'white', width: 40, mb: 2 }} />
        <IconButton sx={{ color: 'white', mb: 2 }}>
          <HelpOutline />
        </IconButton>
        <Avatar sx={{ bgcolor: '#ffb300', width: 32, height: 32, mb: 2, fontSize: 16 }}>
          {user?.username?.[0]?.toUpperCase() || 'A'}
        </Avatar>
        <IconButton
          sx={{
            color: 'white',
            bgcolor: '#ffb300',
            mb: 1,
            '&:hover': {
              bgcolor: '#e6a200',
            },
          }}
          onClick={onLogout}
        >
          <PowerSettingsNew />
        </IconButton>
      </Box>
      {/* Settings Submenu */}
      {(settingsOpen || isSettingsActive) && (
        <Paper
          elevation={3}
          sx={{
            mt: 0,
            ml: -1,
            minWidth: 220,
            borderRadius: 0,
            bgcolor: '#fff',
            height: '100vh',
            alignSelf: 'stretch',
            zIndex: 1,
            p: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ px: 2, py: 1, color: 'grey.700', fontWeight: 600, fontSize: 16 }}>
            Settings
          </Box>
          <Divider sx={{ mb: 1 }} />
          <List>
            {settingsMenu.map((item) => (
              <ListItemButton
                key={item.label}
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  setSettingsOpen(false);
                }}
                sx={{
                  borderRadius: 3,
                  mb: 0.5,
                  bgcolor: location.pathname === item.path ? 'grey.100' : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: 'grey.200',
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    color: location.pathname === item.path ? 'primary' : 'inherit',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
}; 